
import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const fadeInLeft = {
  hidden: { opacity: 0, x: -60 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const fadeInRight = {
  hidden: { opacity: 0, x: 60 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 },
  },
};

interface ServiceDetailProps {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  icon: string;
  image: string;
  features: string[];
  stats: { installations: string; accuracy: string; response: string };
  reversed?: boolean;
}

export default function ServiceDetail({
  id,
  title,
  subtitle,
  description,
  icon,
  image,
  features,
  stats,
  reversed = false,
}: ServiceDetailProps) {
  // React Hook'ları her zaman en üstte ve koşulsuz çağrılmalı
  const ref = useRef<HTMLElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  // Prop kontrolü Hook'lardan sonra yapılmalı
  if (!id || !title || !subtitle || !description || !icon || !image) {
    console.error('ServiceDetail: missing required props', {
      id,
      title,
      subtitle,
      description,
      icon,
      image,
    });
    return null;
  }

  const containerDirection = reversed ? 'rtl' : 'ltr';
  const contentDirection = reversed ? 'ltr' : 'rtl';

  return (
    <section id={id} ref={ref} className="py-20 px-6">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          variants={staggerContainer}
          className="grid md:grid-cols-2 gap-16 items-center"
          style={{ direction: containerDirection }}
        >
          {/* Image Side */}
          <motion.div
            variants={reversed ? fadeInRight : fadeInLeft}
            style={{ direction: contentDirection }}
          >
            <div className="relative">
              <div className="w-full h-[400px] rounded-3xl overflow-hidden">
                <motion.img
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.5 }}
                  src={image}
                  alt={title}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              {/* Stats overlay */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.5, duration: 0.5 }}
                className="absolute -bottom-6 left-6 right-6 bg-white rounded-2xl shadow-xl p-5 grid grid-cols-3 gap-4"
              >
                <div className="text-center">
                  <p className="text-2xl font-bold text-red-600">{stats.installations}</p>
                  <p className="text-xs text-gray-500 mt-1">Kurulum</p>
                </div>
                <div className="text-center border-x border-gray-100">
                  <p className="text-2xl font-bold text-red-600">{stats.accuracy}</p>
                  <p className="text-xs text-gray-500 mt-1">Doğruluk</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-red-600">{stats.response}</p>
                  <p className="text-xs text-gray-500 mt-1">Tepki Süresi</p>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Content Side */}
          <motion.div
            variants={reversed ? fadeInLeft : fadeInRight}
            style={{ direction: contentDirection }}
            className="pt-4"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 rounded-full mb-5">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className={`${icon} text-red-600`}></i>
              </div>
              <span className="text-sm font-medium text-red-700">{subtitle}</span>
            </div>

            <h2 className="text-4xl font-bold text-gray-900 mb-5 leading-tight">{title}</h2>
            <p className="text-gray-600 leading-relaxed mb-8">{description}</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {features.map((feature, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.3 + i * 0.08, duration: 0.4 }}
                  className="flex items-start gap-3"
                >
                  <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                    <i className="ri-check-line text-red-600 text-sm"></i>
                  </div>
                  <span className="text-sm text-gray-700">{feature}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
